/**
 *
 *  Organizations/Show/Settings
 *
 */

import React, { useEffect, useState } from 'react'
// NPM Libraries
import AnimateHeight from 'react-animate-height'
import FloatingLabel from '@joeyparis/react-floating-label-input'
import PropTypes from 'prop-types'
import Toggle from 'react-toggle'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useParams } from 'react-router-dom'

// GraphQL
import useQuery from 'components/UseQuery'
import { gql, useMutation } from '@apollo/client'

// Components
import ContactCustomFields from './ContactCustomFields'
import ContactClientStatuses from './ContactClientStatuses'
import Checkbox from 'components/Checkbox'
import ImageUploader from 'components/ImageUploader'
import FilesUpload from './FilesUpload'
import Lures from './Lures'
import TextEditor from 'components/TextEditor'
import { ToggleContainer } from 'components/Organizations/Show/Integrations'
import { COLORS } from 'global-styles'

const GET_ORGANIZATION = gql`
	query organization($id: ID) {
		organization(id: $id) {
			id
			name
			image_url
			contact_metadata_autocomplete
			email_disclaimer
			landing_page_disclaimer_json
			ready_made_landing_page_disclaimer_json
			facebook_business_url
			email_journalist
			compliance_copy_email
			default_phone_greeting
			default_phone_closing
			operator_notes
			waitlist_phone_closing
			hide_users_email_disclaimer
			hide_users_landing_page_disclaimer
			hide_ready_made_user_landing_page_disclaimer
			hide_ready_made_campaign_landing_page_disclaimer
			custom_crafted_lures_defaults
			ready_made_lures_defaults
			automatically_send_campaigns_email_audit
			automatically_send_weekly_event_summary
			block_custom_sms
			block_custom_emails
			contact_custom_fields {
				id
				field
				options
			}
			compliance_packages {
				id
				name
				url
			}
		}
	}
`

const UPDATE_ORGANIZATION = gql`
	mutation updateOrganization(
		$id: ID!
		$name: String
		$email_disclaimer: String
		$landing_page_disclaimer_json: JSON
		$ready_made_landing_page_disclaimer_json: JSON
		$email_journalist: String
		$facebook_business_url: String
		$compliance_copy_email: String
		$default_phone_greeting: String
		$default_phone_closing: String
		$operator_notes: String
		$waitlist_phone_closing: String
		$image_file: Upload
		$image_url: String
		$hide_users_email_disclaimer: Boolean
		$hide_users_landing_page_disclaimer: Boolean
		$hide_ready_made_user_landing_page_disclaimer: Boolean
		$hide_ready_made_campaign_landing_page_disclaimer: Boolean
		$automatically_send_campaigns_email_audit: Boolean
		$automatically_send_weekly_event_summary: Boolean
		$block_custom_sms: Boolean
		$block_custom_emails: Boolean
	) {
		updateOrganization(
			input: {
				id: $id
				name: $name
				email_disclaimer: $email_disclaimer
				landing_page_disclaimer_json: $landing_page_disclaimer_json
				ready_made_landing_page_disclaimer_json: $ready_made_landing_page_disclaimer_json
				email_journalist: $email_journalist
				compliance_copy_email: $compliance_copy_email
				facebook_business_url: $facebook_business_url
				default_phone_greeting: $default_phone_greeting
				default_phone_closing: $default_phone_closing
				operator_notes: $operator_notes
				waitlist_phone_closing: $waitlist_phone_closing
				image_file: $image_file
				hide_users_email_disclaimer: $hide_users_email_disclaimer
				hide_users_landing_page_disclaimer: $hide_users_landing_page_disclaimer
				hide_ready_made_user_landing_page_disclaimer: $hide_ready_made_user_landing_page_disclaimer
				hide_ready_made_campaign_landing_page_disclaimer: $hide_ready_made_campaign_landing_page_disclaimer
				automatically_send_campaigns_email_audit: $automatically_send_campaigns_email_audit
				automatically_send_weekly_event_summary: $automatically_send_weekly_event_summary
				block_custom_sms: $block_custom_sms
				block_custom_emails: $block_custom_emails
				image_url: $image_url
			}
		) {
			organization {
				id
				name
			}
		}
	}
`

const generate_state = (org) => {
	if (!org) return {}

	return {
		name: org.name,
		email_journalist: org.email_journalist || '',
		facebook_business_url: org.facebook_business_url || '',
		email_disclaimer: org.email_disclaimer || '',
		compliance_copy_email: org.compliance_copy_email || '',
		landing_page_disclaimer_json: org.landing_page_disclaimer_json || [],
		ready_made_landing_page_disclaimer_json: org.ready_made_landing_page_disclaimer_json || [],
		default_phone_greeting: org.default_phone_greeting || '',
		default_phone_closing: org.default_phone_closing || '',
		operator_notes: org.operator_notes || '',
		waitlist_phone_closing: org.waitlist_phone_closing || '',
		hide_users_email_disclaimer: Boolean(org.hide_users_email_disclaimer),
		hide_users_landing_page_disclaimer: Boolean(org.hide_users_landing_page_disclaimer),
		hide_ready_made_user_landing_page_disclaimer: Boolean(org.hide_ready_made_user_landing_page_disclaimer),
		hide_ready_made_campaign_landing_page_disclaimer: Boolean(org.hide_ready_made_campaign_landing_page_disclaimer),
		automatically_send_campaigns_email_audit: Boolean(org.automatically_send_campaigns_email_audit),
		automatically_send_weekly_event_summary: Boolean(org.automatically_send_weekly_event_summary),
		block_custom_emails: Boolean(org.block_custom_emails),
		block_custom_sms: Boolean(org.block_custom_sms),
	}
}

const TextField = ({ value, handleChange, loading, ...input_props }) => {
	const [state, setState] = useState(value)

	useEffect(() => {
		setState(value)
	}, [value])

	const save = () => handleChange(state)
	const clear = () => setState(value)

	const has_changes = state !== value

	return (
		<div className="d-flex align-items-end">
			<div style={{ flex: 1 }}>
				<FloatingLabel value={state} onChange={(e) => setState(e.target.value)} {...input_props} />
			</div>

			{has_changes && (
				<div
					className="mr-1 ml-3"
					style={{
						fontSize: '1.4rem',
						opacity: loading ? '0.4' : 1,
						cursor: loading ? 'default' : 'pointer',
					}}
				>
					<FontAwesomeIcon
						title="Save"
						icon="save"
						className="text-success mr-2"
						onClick={save}
						disabled={loading}
					/>

					<FontAwesomeIcon
						title="Clear"
						icon="times"
						className="text-danger ml-1"
						onClick={clear}
						disabled={loading}
					/>
				</div>
			)}
		</div>
	)
}

const TextAreaField = ({ label, value, handleChange, loading, name }) => {
	const [state, setState] = useState(value)

	useEffect(() => {
		setState(value)
	}, [value])

	const has_changes = state !== value

	const save = () => handleChange(state)
	const clear = () => setState(value)

	return (
		<React.Fragment>
			<div className="d-flex justify-content-between align-items-center">
				<label htmlFor={name} className="form-text text-muted small">
					<b>{label}</b>
				</label>

				{has_changes && (
					<div
						className="mr-1"
						style={{
							fontSize: '1.2rem',
							opacity: loading ? '0.4' : 1,
							cursor: loading ? 'default' : 'pointer',
						}}
					>
						<FontAwesomeIcon
							title="Clear"
							icon="times"
							className="text-danger mr-2"
							onClick={clear}
							disabled={loading}
						/>
						<FontAwesomeIcon
							title="Save"
							icon="save"
							className="text-success"
							onClick={save}
							disabled={loading}
						/>
					</div>
				)}
			</div>

			<textarea
				rows={3}
				name={name}
				className="form-control mb-2"
				value={state}
				onChange={(e) => setState(e.target.value)}
				onBlur={() => handleChange(state)}
			/>
		</React.Fragment>
	)
}

const disclaimer_merge_tag_options = [
	{ text: 'Advisor Name', value: '[USER_FULL_NAME]' },
	{ text: 'Advisor Display Name', value: '[USER_DISPLAY_NAME]' },
	{ text: 'Company Name', value: '[COMPANY_NAME]' },
	{ text: 'Campaign Venues', value: '[EVENT_VENUE]' },
]

const DisclamerEditor = ({ value, handleSave }) => (
	<TextEditor
		default_content={value}
		onSave={handleSave}
		wrapperStyle={{ height: 300, marginBottom: '1rem', background: '#fff' }}
		editorStyle={{
			height: 'calc(100% - 115px)',
		}}
		merge_tag_options={disclaimer_merge_tag_options}
	/>
)

const ExpandableCardContainer = styled.div`
	position: relative;

	.loading-spinner {
		position: absolute;
		top: 18px;
		right: 55px;
	}
`

export const ExpandableCard = ({ title = '', disabled = false, children }) => {
	const [expanded, setExpanded] = useState(false)

	return (
		<ExpandableCardContainer className="card px-4 py-3">
			<div
				className={`d-flex justify-content-between ${!disabled ? 'clickable' : ''}`}
				onClick={() => !disabled && setExpanded(!expanded)}
			>
				<h4 className="m-0">{title}</h4>
				<FontAwesomeIcon
					icon={`caret-circle-${expanded ? 'up' : 'down'}`}
					style={{
						fontSize: '1.3rem',
						marginTop: '3px',
						color: disabled ? COLORS.grey_e : COLORS.grey,
					}}
				/>
			</div>

			<AnimateHeight height={expanded ? 'auto' : 0} duration={200}>
				{children}
			</AnimateHeight>
		</ExpandableCardContainer>
	)
}

function Settings() {
	const { organization_id } = useParams()
	const [state, setState] = useState(generate_state(organization))

	const {
		data: { organization },
		loading,
		refetch,
	} = useQuery(GET_ORGANIZATION, {
		variables: { id: organization_id },
		channels: [organization_id],
		skip: !organization_id,
		fetchPolicy: 'network-only',
	})

	const [updateOrganization, { loading: updatingOrganization }] = useMutation(UPDATE_ORGANIZATION)

	useEffect(() => {
		setState(generate_state(organization))
	}, [organization])

	const handleChangeState = (e) => {
		const { name, value } = e.target

		const new_state = { ...state }

		new_state[name] = value

		setState(new_state)
	}

	const {
		name,
		landing_page_disclaimer_json,
		ready_made_landing_page_disclaimer_json,
		email_disclaimer,
		compliance_copy_email,
		operator_notes,
		default_phone_greeting,
		default_phone_closing,
		waitlist_phone_closing,
		hide_users_email_disclaimer,
		hide_users_landing_page_disclaimer,
		hide_ready_made_user_landing_page_disclaimer,
		hide_ready_made_campaign_landing_page_disclaimer,
		automatically_send_campaigns_email_audit,
		automatically_send_weekly_event_summary,
		block_custom_sms,
		block_custom_emails,
	} = state

	return (
		<main className="container-fluid">
			<div className="row">
				<div className="col-md-6 col-12 mb-3">
					<ExpandableCard title="Organization Details" disabled={loading}>
						<FloatingLabel
							type="text"
							label="Organization Name"
							name="name"
							value={name}
							onChange={handleChangeState}
							onBlur={() =>
								updateOrganization({
									variables: {
										id: organization.id,
										name,
									},
								})
							}
							disabled={updatingOrganization}
						/>

						<div className="mt-5 w-100" style={{ overflowX: 'auto' }}>
							{organization?.id && (
								<ImageUploader
									image_url={organization.image_url}
									uploadImage={(image_file) =>
										updateOrganization({
											variables: { id: organization.id, image_file },
										})
									}
									removeImage={() =>
										updateOrganization({
											variables: { id: organization.id, image_url: '' },
										})
									}
								/>
							)}
						</div>
					</ExpandableCard>

					<ExpandableCard title="Contact Custom Fields" disabled={loading}>
						<ContactCustomFields organization={organization || {}} />
					</ExpandableCard>

					<ExpandableCard title="Contact Client Status" disabled={loading}>
						<ContactClientStatuses organization_id={organization_id} />
					</ExpandableCard>

					<ExpandableCard title="Lures" disabled={loading}>
						<Lures organization_id={organization_id} />
					</ExpandableCard>

					<ExpandableCard title="Broadcast Custom Messages" disabled={loading}>
						<p className="mt-2 mb-1">
							Restrict use of the Broadcast Email/SMS feature, accessible through the actions menu found
							on the Campaign Overview and Event pages. This tool is typically used to send custom email
							or SMS updates to Event registrants. Advisor-level Users cannot access this tool. It is for
							Acquireup employees.
						</p>
						<p style={{ fontStyle: 'italic' }}>
							* Communications are enabled when the switch appears green.
						</p>

						<div className="border mt-3">
							<div className="border-bottom d-flex justify-content-between align-items-center px-3 py-2">
								<span>Emails</span>

								<ToggleContainer>
									<Toggle
										checked={!block_custom_emails}
										onChange={() =>
											updateOrganization({
												variables: {
													id: organization.id,
													block_custom_emails: !block_custom_emails,
												},
											})
										}
										disabled={updatingOrganization}
									/>
								</ToggleContainer>
							</div>

							<div className="d-flex justify-content-between align-items-center px-3 py-2">
								<span>SMS</span>

								<ToggleContainer>
									<Toggle
										checked={!block_custom_sms}
										onChange={() =>
											updateOrganization({
												variables: {
													id: organization.id,
													block_custom_sms: !block_custom_sms,
												},
											})
										}
										disabled={updatingOrganization}
									/>
								</ToggleContainer>
							</div>
						</div>
					</ExpandableCard>
				</div>

				<div className="col-md-6 col-12 mb-3">
					<ExpandableCard title="Journaling" disabled={loading}>
						<TextField
							type="text"
							label="Compliance BCC Email List"
							name="compliance_copy_email"
							value={compliance_copy_email}
							handleChange={(new_value) => {
								updateOrganization({
									variables: {
										id: organization.id,
										compliance_copy_email: new_value,
									},
								})
							}}
						/>
					</ExpandableCard>

					<ExpandableCard title="Organization Disclaimers" disabled={loading}>
						<div className="form-group mt-3">
							<TextAreaField
								label="Email Disclaimer"
								name="email_disclaimer"
								value={email_disclaimer}
								handleChange={(new_value) => {
									updateOrganization({
										variables: {
											id: organization.id,
											email_disclaimer: new_value,
										},
									})
								}}
								loading={updatingOrganization}
							/>

							<Checkbox
								label="Hide Advisor-Level Email Disclaimers"
								checked={hide_users_email_disclaimer}
								onChange={(bool) =>
									updateOrganization({
										variables: {
											id: organization.id,
											hide_users_email_disclaimer: bool,
										},
									})
								}
							/>
						</div>

						<div className="form-group">
							<label htmlFor="landing_page_disclaimer_json" className="form-text text-muted small">
								<b>Landing Page Disclaimer</b>
							</label>

							{!!landing_page_disclaimer_json && (
								<DisclamerEditor
									value={landing_page_disclaimer_json || {}}
									handleSave={(content) => {
										if (landing_page_disclaimer_json !== '') {
											updateOrganization({
												variables: {
													id: organization.id,
													landing_page_disclaimer_json: content,
												},
											})
										}
									}}
								/>
							)}

							<Checkbox
								label="Hide Advisor-Level Landing Page Disclaimers"
								checked={hide_users_landing_page_disclaimer}
								onChange={(bool) =>
									updateOrganization({
										variables: {
											id: organization.id,
											hide_users_landing_page_disclaimer: bool,
										},
									})
								}
							/>
						</div>

						<div className="form-group">
							<label htmlFor="landing_page_disclaimer_json" className="form-text text-muted small">
								<b>567 Landing Page Disclaimer</b>
							</label>

							{ready_made_landing_page_disclaimer_json && (
								<DisclamerEditor
									value={ready_made_landing_page_disclaimer_json || {}}
									handleSave={(content) => {
										if (ready_made_landing_page_disclaimer_json !== '') {
											updateOrganization({
												variables: {
													id: organization.id,
													ready_made_landing_page_disclaimer_json: content,
												},
											})
										}
									}}
								/>
							)}

							<div className="d-flex flex-column">
								<Checkbox
									label="Hide Advisor-Level 567 Landing Page Disclaimers"
									checked={hide_ready_made_user_landing_page_disclaimer}
									onChange={(bool) =>
										updateOrganization({
											variables: {
												id: organization.id,
												hide_ready_made_user_landing_page_disclaimer: bool,
											},
										})
									}
								/>

								<Checkbox
									label="Hide Campaign-Level 567 Landing Page Disclaimers"
									checked={hide_ready_made_campaign_landing_page_disclaimer}
									onChange={(bool) =>
										updateOrganization({
											variables: {
												id: organization.id,
												hide_ready_made_campaign_landing_page_disclaimer: bool,
											},
										})
									}
								/>
							</div>
						</div>
					</ExpandableCard>

					<ExpandableCard title="Call Center Scripts" disabled={loading}>
						<div className="form-group mt-3">
							<TextAreaField
								label="Operator Notes"
								value={operator_notes}
								handleChange={(new_value) => {
									updateOrganization({
										variables: {
											id: organization.id,
											operator_notes: new_value,
										},
									})
								}}
								loading={updatingOrganization}
							/>
						</div>

						<div className="form-group">
							<TextAreaField
								label="Default Phone Greeting for Event Campaigns"
								value={default_phone_greeting}
								handleChange={(new_value) => {
									updateOrganization({
										variables: {
											id: organization.id,
											default_phone_greeting: new_value,
										},
									})
								}}
								loading={updatingOrganization}
							/>
						</div>

						<div className="form-group">
							<TextAreaField
								label="Default Phone Closing for Event Campaigns"
								value={default_phone_closing}
								handleChange={(new_value) => {
									updateOrganization({
										variables: {
											id: organization.id,
											default_phone_closing: new_value,
										},
									})
								}}
								loading={updatingOrganization}
							/>
						</div>

						<div className="form-group">
							<TextAreaField
								label="Waitlist Phone Closing"
								value={waitlist_phone_closing}
								handleChange={(new_value) => {
									updateOrganization({
										variables: {
											id: organization.id,
											waitlist_phone_closing: new_value,
										},
									})
								}}
								loading={updatingOrganization}
							/>
						</div>
					</ExpandableCard>

					<ExpandableCard title="Email Audit & Event Summaries" disabled={loading}>
						<p className="mt-2">
							The Registrant Email Audit is a log of every email sent from LeadJig to each of the
							Registrants within a Campaign. This log is sent 1 day after the last Event within each
							Campaign. It delivers to the Campaign Owner as a CSV spreadsheet.
						</p>

						<div className="border d-flex justify-content-between align-items-center px-3 py-2 mt-3">
							<span>Automatically Send Registrant Email Audit</span>

							<ToggleContainer>
								<Toggle
									checked={automatically_send_campaigns_email_audit}
									onChange={() =>
										updateOrganization({
											variables: {
												id: organization.id,
												automatically_send_campaigns_email_audit:
													!automatically_send_campaigns_email_audit,
											},
										})
									}
									disabled={updatingOrganization}
								/>
							</ToggleContainer>
						</div>

						<p className="mt-4">
							The Weekly Event Summary automatically sends an Attendee Export to the Campaign Owner on
							Mondays at 11:00 AM. This summary is also a CSV spreadsheet.
						</p>

						<div className="border d-flex justify-content-between align-items-center px-3 py-2">
							<span>Automatically Send Weekly Event Summaries</span>

							<ToggleContainer>
								<Toggle
									checked={automatically_send_weekly_event_summary}
									onChange={() =>
										updateOrganization({
											variables: {
												id: organization.id,
												automatically_send_weekly_event_summary:
													!automatically_send_weekly_event_summary,
											},
										})
									}
									disabled={updatingOrganization}
								/>
							</ToggleContainer>
						</div>
					</ExpandableCard>

					{organization && <FilesUpload organization={organization} refetch={refetch} />}
				</div>
			</div>
		</main>
	)
}

Settings.propTypes = {}

ExpandableCard.propTypes = {
	title: PropTypes.string,
	children: PropTypes.element,
	disabled: PropTypes.bool,
}

TextAreaField.propTypes = {
	label: PropTypes.string,
	value: PropTypes.string,
	name: PropTypes.string,
	handleChange: PropTypes.func,
	loading: PropTypes.bool,
}

TextField.propTypes = {
	value: PropTypes.string,
	handleChange: PropTypes.func,
	loading: PropTypes.bool,
}

DisclamerEditor.propTypes = {
	value: PropTypes.string,
	handleSave: PropTypes.func,
}

export default Settings
